import { translate } from "@/plugins/i18n";
import { isEnum } from "@/app/services/baseEnumType";
import { LocalDate, Instant } from "@js-joda/core";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type Guid from "@/app/types/common/guid";
import type { Ref } from "vue";
import { computed } from "vue";
import { cloneDeepWith } from "lodash-es";
import Key from "@/app/types/common/key";

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

type IsNullable<T, K> = null extends T ? K : never;
export type NullableKeys<T> = {
  [K in keyof T]-?: IsNullable<T[K], K>;
}[keyof T];

export type Matcher = (itemValue: any, search: string) => boolean;

export const stringMatcher: Matcher = (itemValue: any, search: string) => {
  return (
    itemValue &&
    itemValue.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
  );
};

/**
 * Returns a string formatted how the user should see it on screen
 * @param item The item to format
 * @returns formatted string
 */
export const localeFormattedStringExtractor = (item: any): string => {
  if (item instanceof LocalDate) {
    return tenantService.formatDate(item);
  }
  if (item instanceof Instant) {
    return tenantService.formatDateTime(item);
  }
  if (isEnum(item)) {
    return item.translationKey
      ? translate(Key.fromString(item.translationKey)).toString()
      : item.value;
  }
  if (typeof item === "number") {
    // Remove separators from numbers (e.g. 1,234.56 => 1234.56)
    return item.toString();
  }
  return item.toLocaleString();
};

export function guidHasValue(id: Guid | null | undefined): id is Guid {
  return !!id && !id.isEmpty();
}

export const useArrayWithPlaceholderItem = <T>(
  items: Ref<readonly Readonly<T>[]>,
  placeholderItem: T,
  idExtractor: (item: T) => Guid
) => {
  const itemsWithPlaceholder = computed(() => [
    ...items.value,
    placeholderItem,
  ]);

  return {
    items: itemsWithPlaceholder,
    placeholderId: idExtractor(placeholderItem),
  };
};

/**
 * To prevent multiple instances BmEnums and therefore not equal, this to preserve the BmEnum instances
 *
 * @param item - Item To clone
 * @returns Cloned Item
 */
export const cloneDeepPreserveBmEnum = (item: any) => {
  const cloneItemPreserveBmEnum = (value: any) => {
    if (value != null && isEnum(value)) {
      return value;
    }
  };
  return cloneDeepWith(item, cloneItemPreserveBmEnum);
};

export const sortByFieldWithTiebreak = <T>(
  initialSortByFieldExtractor: (line: T) => string | null | undefined,
  tiebreakFieldExtractor: (line: T) => string | null | undefined
) => {
  return (lineA: T, lineB: T): number => {
    // Sort initial field
    const initialFieldA = initialSortByFieldExtractor(lineA);
    const initialFieldB = initialSortByFieldExtractor(lineB);

    const initialCompare = (initialFieldA ?? "").localeCompare(
      initialFieldB ?? ""
    );

    if (initialCompare === 0) {
      // Use tiebreak field if initial field is the same
      return (tiebreakFieldExtractor(lineA) ?? "").localeCompare(
        tiebreakFieldExtractor(lineB) ?? ""
      );
    }

    return initialCompare;
  };
};
